import { useEffect, useState } from 'react';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import TextField from 'components/fields/TextField';
import jobRoleServices from 'ApiService/Placement/jobRoleServices';
import departmentService from 'ApiService/SuperAdmin/departmentService';
import toast from 'react-hot-toast';

const Index = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [editingRole, setEditingRole] = useState(null);

  const [roleForm, setRoleForm] = useState({
    roleName: '',
    description: '',
    skills: '',
    experienceLevel: 'Fresher',
  });

  const experienceLevels = [
    'Fresher',
    'Junior',
    'Mid-level',
    'Senior',
    'Lead',
    'Expert',
    'Intern',
  ];

  const fetchDepartmentsAndRoles = async () => {
    setLoading(true);
    try {
      // Fetch all departments
      const deptResponse = await departmentService.getAllDepartments();
      if (deptResponse.data.success) {
        const fetchedDepartments = deptResponse.data.data;

        // Fetch roles for each department
        const departmentsWithRoles = await Promise.all(
          fetchedDepartments.map(async (dept) => {
            try {
              const rolesResponse = await jobRoleServices.getRolesByDepartment(
                dept._id
              );
              return {
                ...dept,
                roles: rolesResponse.data.success
                  ? rolesResponse.data.roles
                  : [],
              };
            } catch (err) {
              return { ...dept, roles: [] };
            }
          })
        );

        setDepartments(departmentsWithRoles);
      } else {
        toast.error('Failed to fetch departments');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartmentsAndRoles();
  }, []);

  const handleRoleChange = (e) => {
    const { name, value } = e.target;
    setRoleForm({ ...roleForm, [name]: value });
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      const roleData = {
        ...roleForm,
        departmentId: selectedDepartment._id,
        skills: roleForm.skills
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };

      let response;
      if (editingRole) {
        response = await jobRoleServices.updateRole(editingRole._id, roleData);
      } else {
        response = await jobRoleServices.createRole(roleData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowRoleModal(false);
        resetRoleForm();
        fetchDepartmentsAndRoles();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      const response = await jobRoleServices.deleteRole(roleId);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchDepartmentsAndRoles();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete role');
    }
  };

  const resetRoleForm = () => {
    setRoleForm({
      roleName: '',
      description: '',
      skills: '',
      experienceLevel: 'Fresher',
    });
    setEditingRole(null);
  };

  const openAddRole = (department) => {
    setSelectedDepartment(department);
    resetRoleForm();
    setShowRoleModal(true);
  };

  const openEditRole = (department, role) => {
    setSelectedDepartment(department);
    setEditingRole(role);
    setRoleForm({
      roleName: role.roleName || '',
      description: role.description || '',
      skills: role.skills?.join(', ') || '',
      experienceLevel: role.experienceLevel || 'Fresher',
    });
    setShowRoleModal(true);
  };

  return (
    <div className="mt-4 h-full w-full">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
            Job Role Management
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Create and manage job roles under different departments
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {departments.map((department) => (
            <Card key={department._id} extra="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                      {department.name}
                    </h3>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openAddRole(department)}
                    className="rounded-lg bg-brand-100 px-3 py-1 text-sm font-medium text-brand-700 hover:bg-brand-200"
                  >
                    + Add Role
                  </button>
                </div>
              </div>

              {department.roles && department.roles.length > 0 && (
                <div className="mt-4 border-t pt-4 dark:border-white/10">
                  <h4 className="mb-3 text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Job Roles ({department.roles.length})
                  </h4>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {department.roles.map((role) => (
                      <div
                        key={role._id}
                        className="rounded-lg border border-gray-200 p-3 dark:border-white/10"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-medium text-navy-700 dark:text-white">
                              {role.roleName}
                            </h5>
                            <p className="text-xs text-gray-500">
                              {role.experienceLevel}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => openEditRole(department, role)}
                              className="text-xs text-brand-500 hover:text-brand-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteRole(role._id)}
                              className="text-xs text-red-500 hover:text-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        {role.description && (
                          <p className="mt-1 text-xs text-gray-500">
                            {role.description}
                          </p>
                        )}
                        {role.skills && role.skills.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {role.skills.map((skill, idx) => (
                              <span
                                key={idx}
                                className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-navy-700 dark:text-gray-300"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!department.roles || department.roles.length === 0) && (
                <div className="mt-4 border-t pt-4 dark:border-white/10">
                  <p className="text-sm text-gray-500">No roles added yet.</p>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {!loading && departments.length === 0 && (
        <div className="flex h-40 items-center justify-center">
          <p className="text-gray-500">No departments found.</p>
        </div>
      )}

      {showRoleModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-xl bg-white p-6 dark:bg-navy-800">
            <h3 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
              {editingRole
                ? 'Edit Role'
                : `Add Role to ${selectedDepartment?.name}`}
            </h3>
            <form onSubmit={handleRoleSubmit} className="space-y-4">
              <InputField
                label="Role Name *"
                name="roleName"
                value={roleForm.roleName}
                onChange={handleRoleChange}
                placeholder="e.g., React Developer, Java Developer"
                required
              />

              <div>
                <label className="mb-2 ml-3 text-sm font-bold text-navy-700 dark:text-white">
                  Experience Level
                </label>
                <select
                  name="experienceLevel"
                  value={roleForm.experienceLevel}
                  onChange={handleRoleChange}
                  className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                >
                  {experienceLevels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              <TextField
                label="Description"
                id="description"
                value={roleForm.description}
                onChange={handleRoleChange}
                placeholder="Enter role description"
                rows={2}
              />

              <InputField
                label="Skills (comma separated)"
                name="skills"
                value={roleForm.skills}
                onChange={handleRoleChange}
                placeholder="e.g., React, JavaScript, Node.js"
              />

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRoleModal(false);
                    resetRoleForm();
                  }}
                  className="flex-1 rounded-xl bg-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-400 dark:bg-navy-600 dark:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-xl bg-brand-500 px-4 py-2 font-medium text-white hover:bg-brand-600"
                >
                  {editingRole ? 'Update' : 'Add Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
